import React, { useState, useRef, useEffect } from "react";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { AlarmClockPlusIcon, FileDigitIcon, FileDownIcon, Share2Icon, ShareIcon, Trash2Icon, Undo2Icon } from "lucide-react";
import style from "../OrderInfoExpeditor.module.scss";
import MoreIcon from "../../../shared/icons/MoreIcon";
import { File, User } from "../../../../services";
import CancelApp from "../../../order/info/CancelApp";
import OrdersDelete from "../../../order/info/OrdersDelete";
import SendToMarketplaceModal from "../../order-info/components/SendToMarketplaceModal";
import ExtendTenderEndDate from "../../order-info/components/ExtendTenderEndDate/ExtendTenderEndDate";
import AddProjectNumberModal from "../../order-info/components/AddProjectNumberModal/AddProjectNumberModal";
import { useClickOutside } from "../../../../hooks/useClickOutside";
import CopyIconNew from "../../../shared/icons/CopyIconNew";
import EditIconNew from "../../../shared/icons/EditIconNew";
import EditAmountModal from "./OrderBidBlock/EditAmountModal";

const OrderInfoExpeditorMoreMenu = ({ item, onForceUpdate, onRefreshOrder, setIsOpen }) => {
  const { t, i18n } = useTranslation();
  const { user } = useSelector((state) => state);
  const moreRef = useRef(null);
  const copyLinkRef = useRef(null);

  const [isOpenDropdowns, setIsOpenDropdowns] = useState(false);
  const [isCancel, setCancelModal] = useState(false);
  const [isDelete, setDelete] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [isShareDropdownOpen, setIsShareDropdownOpen] = useState(false);
  const [showConfirmMarketplaceModal, setShowConfirmMarketplaceModal] = useState(false);
  const [showExtendTenderModal, setShowExtendTenderModal] = useState(false);
  const [showAddProjectNumberModal, setShowAddProjectNumberModal] = useState(false);
  const [showEditAmountModal, setShowEditAmountModal] = useState(false);

  // "Изменить сумму" показываем во всех статусах, кроме перечисленных
  const shouldShowEditAmount = () =>
    !["awaitingoffer", "assigned", "return", "completedbyexecutor", "completed"].includes(item?.status);

  const shouldShowButton = () =>
    ["published", "awaitingoffer", "assigned", "moderation", "awaiting_payment", "rejected", "canceled"].includes(
      item?.status,
    );

  const shouldShowWithdrawButton = () =>
    [
      "published",
      "awaitingoffer",
      "assigned",
      "assignedexecutor",
      "booked",
      "confirmed",
      "moderation",
      "awaiting_payment",
      "rejected",
    ].includes(item?.status);

  const handleClickOutside = (event) => {
    if (moreRef.current && !moreRef.current.contains(event.target)) {
      setIsOpenDropdowns(false);
    }
  };

  const downloadOrderPdf = async () => {
    try {
      const response = await File.getOrderPdf(item.id, i18n.language);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `order_${item.number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      toast.error(t("orderInfoExpeditor.moreMenu.downloadPdfError"));
    }
    setIsOpenDropdowns(false);
  };

  const handleShareClick = async () => {
    if (shareLink) {
      setIsShareDropdownOpen(!isShareDropdownOpen);
    } else {
      try {
        const response = await User.guestLogin(item.id);
        const accessToken = response.data.accessToken;
        const link = `${window.location.origin}/order/share/${accessToken}`;
        setShareLink(link);
        setIsShareDropdownOpen(true);
      } catch (error) {
        console.error(error);
        toast.error(t("orderInfoExpeditor.moreMenu.shareLinkError"));
      }
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success(t("orderInfoExpeditor.moreMenu.linkCopied"));
    setIsShareDropdownOpen(false);
  };

  const handleMarketplaceSuccess = () => {
    setShowConfirmMarketplaceModal(false);
    if (onForceUpdate) onForceUpdate();
  };

  useClickOutside(copyLinkRef, () => setIsShareDropdownOpen(false));

  useEffect(() => {
    if (isOpenDropdowns) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpenDropdowns]);

  useEffect(() => {
    setShareLink("");
  }, [item]);

  if (!item) return null;

  return (
    <div className={style.more_wrapper} ref={moreRef}>
      <MoreIcon
        width={24}
        height={24}
        onClick={() => setIsOpenDropdowns(!isOpenDropdowns)}
        style={{ cursor: "pointer" }}
      />

      <div className={cn(style.more_dropdown, { [style.more_dropdown_open]: isOpenDropdowns })}>
        {user?.companyId === item?.companyId &&
          item?.status === "awaitingoffer" &&
          item?.isTender && (
            <div
              className={style.more_dropdown_item}
              onClick={() => {
                setShowConfirmMarketplaceModal(true);
                setIsOpenDropdowns(false);
              }}
            >
              <ShareIcon size={18} color="#6D7885" />
              {t("orderInfoExpeditor.moreMenu.sendToMarket")}
            </div>
          )}

        {user?.companyId === item?.companyId && item?.status === "awaitingoffer" && (
          <div
            className={style.more_dropdown_item}
            onClick={() => {
              setShowExtendTenderModal(true);
              setIsOpenDropdowns(false);
            }}
          >
            <AlarmClockPlusIcon size={18} color="#6D7885" />
            {t("orderInfoExpeditor.moreMenu.extendTender")}
          </div>
        )}

        {user?.companyId === item?.companyId && (
          <div className={style.more_dropdown_item} onClick={downloadOrderPdf}>
            <FileDownIcon size={18} color="#6D7885" />
            {t("orderInfoExpeditor.moreMenu.downloadPdf")}
          </div>
        )}

        {user?.companyId === item?.companyId && (
          <div
            className={style.more_dropdown_item}
            onClick={() => {
              setShowAddProjectNumberModal(true);
              setIsOpenDropdowns(false);
            }}
          >
            <FileDigitIcon size={18} color="#6D7885" />
            {t("orderInfoExpeditor.moreMenu.addProjectNumber")}
          </div>
        )}

        {shouldShowEditAmount() && (
          <div
            className={style.more_dropdown_item}
            onClick={() => {
              setShowEditAmountModal(true);
              setIsOpenDropdowns(false);
            }}
          >
            <EditIconNew width={18} height={18} />
            {t("orderInfoExpeditor.moreMenu.changeAmount")}
          </div>
        )}

        {item?.status !== "completed" && (
          <div ref={copyLinkRef}>
            <div className={style.more_dropdown_item} onClick={handleShareClick}>
              <Share2Icon size={18} color="#6D7885" />
              {t("orderInfoExpeditor.moreMenu.shareOrder")}
            </div>
            {shareLink && isShareDropdownOpen && (
              <div className={style.share_box}>
                <input type="text" value={shareLink} readOnly className={style.share_input} />
                <button className={style.share_copy_btn} onClick={handleCopyLink}>
                  <CopyIconNew />
                  <span>{t("orderInfoExpeditor.moreMenu.copy")}</span>
                </button>
              </div>
            )}
          </div>
        )}

        {shouldShowWithdrawButton() && user?.companyId === item?.companyId && (
          <div
            className={cn(style.more_dropdown_item, style.more_dropdown_item_danger)}
            onClick={() => setCancelModal(true)}
          >
            <Undo2Icon size={18} color="#E53935" />
            {t("orderInfoExpeditor.moreMenu.withdrawOrder")}
          </div>
        )}

        {/* {shouldShowButton() && user?.companyId === item?.companyId && (
          <div
            className={cn(style.more_dropdown_item, style.more_dropdown_item_danger)}
            onClick={() => setDelete(true)}
          >
            <Trash2Icon size={18} color="#E53935" />
            {t("orderInfoExpeditor.moreMenu.deleteOrder")}
          </div>
        )} */}
      </div>

      {isCancel && <CancelApp item={item} onClose={() => setCancelModal(false)} onUpdateData={() => onForceUpdate?.()} />}
      {isDelete && (
        <OrdersDelete
          item={item}
          onClose={() => setDelete(false)}
          onUpdateData={() => onForceUpdate?.()}
          setIsOpen={setIsOpen}
        />
      )}

      <SendToMarketplaceModal
        isOpen={showConfirmMarketplaceModal}
        orderId={item.id}
        tenderDateEnd={item?.tenderDateEnd}
        onSuccess={handleMarketplaceSuccess}
        onCancel={() => setShowConfirmMarketplaceModal(false)}
      />

      <ExtendTenderEndDate
        isOpen={showExtendTenderModal}
        orderId={item.id}
        tenderDateEnd={item?.tenderDateEnd}
        assignmentMethod={item?.assignmentMethod}
        onSuccess={() => {
          setShowExtendTenderModal(false);
          onForceUpdate?.();
        }}
        onCancel={() => setShowExtendTenderModal(false)}
      />

      <AddProjectNumberModal
        isOpen={showAddProjectNumberModal}
        orderId={item.id}
        onSuccess={() => {
          setShowAddProjectNumberModal(false);
          onForceUpdate?.();
        }}
        onCancel={() => setShowAddProjectNumberModal(false)}
      />

      <EditAmountModal
        onRefreshOrder={onRefreshOrder}
        isOpen={showEditAmountModal}
        orderId={item?.id}
        currentPrice={item?.price}
        currency={item?.currency}
        onClose={() => setShowEditAmountModal(false)}
        onSuccess={() => {
          setShowEditAmountModal(false);
          onForceUpdate?.();
        }}
      />
    </div>
  );
};

export default OrderInfoExpeditorMoreMenu;
